import { useEffect } from 'react'
import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useSceneSheet } from '../../domains/scenes/queries'
import { useSceneEditing } from '../../domains/scenes/useSceneEditing'
import { useSceneSessionStore } from '../../domains/scenes/sessionState'
import { CastCard } from '../../domains/scenes/sections/CastCard'
import { ExchangeCard } from '../../domains/scenes/sections/ExchangeCard'
import { PanelsCard } from '../../domains/scenes/sections/PanelsCard'
import { SceneAspectsCard } from '../../domains/scenes/sections/SceneAspectsCard'
import { SnapshotCard } from '../../domains/scenes/sections/SnapshotCard'
import { TurnOrderCard } from '../../domains/scenes/sections/TurnOrderCard'
import { sceneWhereAndWhen } from '../../domains/scenes/types'
import { Banner, Button, Card } from '../../design/components'
import styles from './play.module.css'

/**
 * Design spec §5.3 — the scene tracker: the table view during a conflict.
 *
 * Card order is the spec's, read top-down as a table actually uses it: the exchange
 * counter, then the aspects in play, then whose turn it is, then who is here. Panels and
 * the snapshot are prep rather than play, so they collapse at the bottom.
 *
 * **Half this screen is device-local.** The exchange counter, the turn order, the
 * free-invoke counts and the hostile tint have no API resource at all (implementation
 * plan §3), so they live in `sessionState.ts`, scoped to this tab, and every card that
 * shows one says so. Everything else — the scene, its aspects, its cast — is server
 * state patched through the envelope like every other sheet in the app.
 */
export function SceneTrackerPage() {
  const params = useParams<{ sceneId: string }>()
  const sceneId = Number(params.sceneId)

  const query = useSceneSheet(sceneId)
  const editing = useSceneEditing(sceneId, query.data?.isGameMaster === true)
  const openScene = useSceneSessionStore((state) => state.openScene)

  // Remembering the scene is what lets the Scene tab land on it next time. Device-local
  // for the same reason the counters are: "the scene in play" is a fact about this phone
  // at this table, not something the API knows.
  useEffect(() => {
    if (query.isSuccess) openScene(sceneId)
  }, [query.isSuccess, openScene, sceneId])

  if (Number.isNaN(sceneId)) {
    return <SceneMessage message="That scene id is not a number." />
  }

  if (query.isPending) {
    return <SceneMessage message="Loading…" subtitle="FETCHING SCENE" />
  }

  if (query.isError) {
    const missing =
      query.error instanceof ApiError &&
      (query.error.kind === 'not-found' || query.error.kind === 'forbidden')

    return (
      <SceneMessage
        message={
          missing ? 'No such scene, or it is not one of yours.' : 'Could not load this scene.'
        }
        tone={missing ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data
  const core = sheet.sections.core

  return (
    <AppShell title={core?.title ?? 'Scene'} subtitle={sceneWhereAndWhen(core)?.toUpperCase()}>
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      <ExchangeCard sceneId={sceneId} />
      <SceneAspectsCard sceneId={sceneId} aspects={sheet.sections.aspects} editing={editing} />
      <TurnOrderCard sceneId={sceneId} characters={sheet.sections.characters} />
      <CastCard sceneId={sceneId} characters={sheet.sections.characters} editing={editing} />

      <PanelsCard sceneId={sceneId} panels={sheet.sections.panels} editing={editing} />
      <SnapshotCard
        sceneId={sceneId}
        hasSnapshot={core?.hasSnapshot === true}
        characters={sheet.sections.characters}
        editing={editing}
      />

      {core?.summary !== undefined && core.summary !== '' && (
        <Card variant="bio" label="Summary">
          <p style={{ whiteSpace: 'pre-wrap' }}>{core.summary}</p>
        </Card>
      )}

      <Link to={`/cases/${sheet.caseId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the case
        </Button>
      </Link>
    </AppShell>
  )
}

function SceneMessage({
  message,
  subtitle,
  tone = 'info',
}: {
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="Scene" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}
